/**
 * @fileoverview The OS/browser "reduce motion" preference, reactively.
 *
 * Lifted from `FreeSupplier/app/components/CommsTemplateDropzone.jsx`, which
 * wrote it first and now imports it: a second component needing the same
 * preference is the moment it stops being that file's business.
 *
 * Read here rather than through a `motion-reduce:` Tailwind variant on
 * purpose. This app serves a PREBUILT Tailwind bundle, so a variant the bundle
 * never generated is not a weaker style — it is no style at all, which is how
 * the guide's tooltip once rendered permanently invisible. A preference the
 * component can branch on cannot fail that way.
 */

import { useEffect, useState } from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

/**
 * jsdom (this app's test environment) does not implement `matchMedia` at all,
 * so every access is guarded — tests simply get `false` (motion allowed)
 * rather than throwing.
 *
 * @returns {boolean}
 */
export function usePrefersReducedMotion() {
  const readPreference = () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function'
      ? window.matchMedia(QUERY).matches
      : false;

  const [prefersReduced, setPrefersReduced] = useState(readPreference);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mql = window.matchMedia(QUERY);
    const handleChange = (event) => setPrefersReduced(event.matches);
    if (mql.addEventListener) {
      mql.addEventListener('change', handleChange);
      return () => mql.removeEventListener('change', handleChange);
    }
    // Safari < 14 fallback — MediaQueryList predates addEventListener there.
    mql.addListener(handleChange);
    return () => mql.removeListener(handleChange);
  }, []);

  return prefersReduced;
}

export default usePrefersReducedMotion;
